import React, { useState } from 'react';
import { exportActions } from '../taxComplianceTypes';
import { TaxComplianceOverview, ComplianceActivity, downloadActivityCsv } from '../../../../services/taxComplianceApi';
import { triggerBlobDownload } from '../../../../utils/downloadBlob';
import styles from './ExportActions.module.css';

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
  </svg>
);

const FileIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
  </svg>
);

interface ExportActionsProps {
  overview?: TaxComplianceOverview | null;
  activity?: ComplianceActivity[];
}

function escapeCsv(v: string): string {
  if (v.includes(',') || v.includes('"') || v.includes('\n')) {
    return `"${v.replace(/"/g, '""')}"`;
  }
  return v;
}

function downloadFile(name: string, content: string, mime: string): void {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export const ExportActions: React.FC<ExportActionsProps> = ({ overview }) => {
  const [filed, setFiled] = useState(false);
  const [exportingReport, setExportingReport] = useState(false);

  // Sec 12 — real server-side export: GET /tax-compliance/activity?format=csv,
  // replacing the old approach of re-serializing whatever activity rows
  // happened to already be in memory on the client.
  const downloadReport = async () => {
    setExportingReport(true);
    try {
      const blob = await downloadActivityCsv(100);
      triggerBlobDownload(blob, `tax-compliance-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (err) {
      console.error('Tax compliance export failed', err);
    } finally {
      setExportingReport(false);
    }
  };

  const downloadStateFiling = () => {
    const header = ['State', 'Active Jurisdictions', 'Configured'];
    const rows = [
      ['ALL', String(overview?.active_jurisdictions ?? 0), `${overview?.states_configured ?? 0}/${overview?.states_total ?? 50}`],
    ];
    const csv = [header.join(','), ...rows.map((r) => r.map(escapeCsv).join(','))].join('\n');
    downloadFile(`state-filing-${new Date().toISOString().slice(0, 10)}.csv`, csv, 'text/csv');
  };

  const download1099 = () => {
    const header = ['Vendor', 'TIN', 'Total Paid', 'Year'];
    const csv = [header.join(','), '# Populate with payee records when 1099 module is wired'].join('\n');
    downloadFile(`1099-package-${new Date().getFullYear()}.csv`, csv, 'text/csv');
  };

  const handlers: Record<string, () => void> = {
    'Download Tax Report (CSV)': downloadReport,
    'Export CSV (State Filing Format)': downloadStateFiling,
    'Export 1099 E-File Package': download1099,
  };

  return (
    <div className={styles.wrapper}>
      <p className={styles.sectionLabel}>GENERATE &amp; EXPORT</p>
      <div className={styles.buttonList}>
        {exportActions.map((action) => (
          <button
            key={action.label}
            type="button"
            className={action.variant === 'primary' ? styles.primaryBtn : styles.secondaryBtn}
            onClick={handlers[action.label]}
            disabled={action.label === 'Download Tax Report (CSV)' && exportingReport}
          >
            <span className={styles.btnIcon}>
              {action.variant === 'primary' ? <DownloadIcon /> : <FileIcon />}
            </span>
            {action.label === 'Download Tax Report (CSV)' && exportingReport ? 'Exporting…' : action.label}
          </button>
        ))}
      </div>

      <p className={styles.sectionLabel} style={{ marginTop: 20 }}>FILING</p>
      <button
        type="button"
        className={styles.filingBtn}
        onClick={() => setFiled(true)}
        disabled={filed}
      >
        <CheckIcon />
        {filed ? 'Filed' : 'Mark Return as Filed'}
      </button>
    </div>
  );
};
